import {
  AutocompleteWithLabel,
  CheckboxWithLabel,
  FreeSoloAutocompleteWithLabel,
  InputWithLabel,
  SelectWithLabel
} from '@adotel/components'
import { Box, Button, MenuItem } from '@material-ui/core'
import { useState } from 'react'

import SectionTitle from '../../components/SectionTitle'
import { t } from './shared'
import useStyles from './styles/CategoryForm.styles'
import { ResourceRow } from './types'

/**
 * `domain` and `subjectKind` are `String!` on the GraphQL input, but the
 * service constrains both with a CHECK — `notification_categories` in
 * 0001_init. So the wire type permits anything and the database refuses most of
 * it: a free-text field would let an operator submit a value that fails at the
 * far end, with nothing on this screen explaining why.
 *
 * Duplicating the constraint here is the tradeoff. It is worth it because the
 * alternative is a form that accepts input the system rejects, and these two
 * lists have changed once between them since 0001.
 */
const DOMAINS = [
  'distribution',
  'operations',
  'payments',
  'security',
  'auth',
  'platform'
] as const

const SUBJECT_KINDS = ['PLATFORM', 'TENANT', 'ENTITY'] as const

/**
 * UNKNOWN is deliberately absent. The GraphQL enum carries it so an
 * unrecognised severity from a newer service build degrades to a displayable
 * value instead of failing the page — it is a READ-side fallback, and offering
 * it here would let an operator write a severity that means "unparseable".
 */
const SEVERITIES = ['INFO', 'WARNING', 'CRITICAL'] as const

const CHANNELS = ['IN_APP', 'SMS', 'EMAIL'] as const

interface CategoryFormProps {
  initial?: ResourceRow
  saving: boolean
  onSubmit: (category: ResourceRow) => void
}

/**
 * A real form for notification categories, replacing the JSON textarea (§6).
 *
 * Every field is flat — enums, switches, a multi-select, a string list and a
 * per-locale name — which is what separates this resource from Rules (CEL
 * source) and Subscriptions (a required nested bindings array).
 */
export default function CategoryForm({
  initial,
  saving,
  onSubmit
}: CategoryFormProps) {
  const classes = useStyles()
  const creating = !initial
  const [draft, setDraft] = useState<ResourceRow>(
    initial ?? {
      key: '',
      domain: 'operations',
      defaultSeverity: 'INFO',
      defaultChannels: ['IN_APP'],
      userMutable: true,
      retainBody: true,
      subjectKind: 'ENTITY',
      variableContract: [],
      displayName: { EN: '', FA: '' }
    }
  )

  const set = (field: string, value: unknown) =>
    setDraft((current) => ({ ...current, [field]: value }))

  const displayName = (draft.displayName ?? {}) as Record<string, string>
  const setDisplayName = (locale: string, value: string) =>
    set('displayName', { ...displayName, [locale]: value })

  const channels = (draft.defaultChannels as string[]) ?? []

  return (
    <Box mt={2}>
      <SectionTitle title={t('Create or update category')} />

      {/* The selects below name their controls since @adotel/components 1.3.0.
          Before that the visible label associated with nothing. */}
      <div className={classes.grid}>
        <InputWithLabel
          id="category-key"
          name="category-key"
          label={t('Key')}
          // Locked while editing. `key` is this resource's identity — the
          // mutation upserts on it — so changing it does not rename the
          // category, it writes a second one and leaves the original. Blank
          // forms made that obvious; a prefilled one makes the field look like
          // part of the record being edited.
          disabled={!creating}
          value={String(draft.key ?? '')}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            set('key', event.currentTarget.value)
          }
        />
        <SelectWithLabel
          id="category-domain"
          name="category-domain"
          label={t('Domain')}
          value={String(draft.domain ?? '')}
          onChange={(event: { target: { value: unknown } }) =>
            set('domain', event.target.value)
          }>
          {DOMAINS.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </SelectWithLabel>
        <SelectWithLabel
          id="category-severity"
          name="category-severity"
          label={t('Default severity')}
          value={String(draft.defaultSeverity ?? '')}
          onChange={(event: { target: { value: unknown } }) =>
            set('defaultSeverity', event.target.value)
          }>
          {SEVERITIES.map((option) => (
            <MenuItem key={option} value={option}>
              {t(option)}
            </MenuItem>
          ))}
        </SelectWithLabel>
        <SelectWithLabel
          id="category-subject-kind"
          name="category-subject-kind"
          label={t('Subject kind')}
          value={String(draft.subjectKind ?? '')}
          onChange={(event: { target: { value: unknown } }) =>
            set('subjectKind', event.target.value)
          }>
          {SUBJECT_KINDS.map((option) => (
            <MenuItem key={option} value={option}>
              {t(option)}
            </MenuItem>
          ))}
        </SelectWithLabel>
      </div>

      <AutocompleteWithLabel
        label={t('Default channels')}
        options={CHANNELS.map((channel) => ({
          label: t(channel),
          value: channel
        }))}
        value={channels.map((channel) => ({
          label: t(channel),
          value: channel
        }))}
        onChange={(_event: unknown, value: { value: string }[]) =>
          set(
            'defaultChannels',
            value.map((option) => option.value)
          )
        }
      />

      {/* The contract a template may reference. Same validation as a template's
          own variables list — a name that is not a Go template identifier
          cannot be substituted, and the placeholder reaches the user verbatim. */}
      <FreeSoloAutocompleteWithLabel
        label={t('Variable contract')}
        options={[]}
        value={(draft.variableContract as string[]) ?? []}
        onChange={(_event: unknown, value: string[]) =>
          set('variableContract', value)
        }
        regexPattern={/^[a-z][a-z0-9_]*$/}
        message={t('Use lowercase letters, digits and underscores.')}
      />

      {/* displayName is a JSONObject keyed by locale. Two inputs rather than a
          JSON blob: the portal ships both languages, and a missing FA key shows
          an operator a blank name with no hint that a field was expected. */}
      <div className={classes.grid}>
        <InputWithLabel
          id="category-name-en"
          name="category-name-en"
          label={t('Display name (EN)')}
          value={displayName.EN ?? ''}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setDisplayName('EN', event.currentTarget.value)
          }
        />
        <InputWithLabel
          id="category-name-fa"
          name="category-name-fa"
          label={t('Display name (FA)')}
          value={displayName.FA ?? ''}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setDisplayName('FA', event.currentTarget.value)
          }
        />
      </div>

      <div className={classes.switches}>
        {/* CheckboxWithLabel, not SwitchWithText: the latter is a two-option
            segmented control (items: [string, string], selectedItemIndex) and
            not a boolean toggle, despite the name. PreferencesPanel on this
            same screen already uses the checkbox for booleans. */}
        <CheckboxWithLabel
          label={t('User can change this')}
          noHelperText
          checked={Boolean(draft.userMutable)}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            set('userMutable', event.currentTarget.checked)
          }
        />
        <CheckboxWithLabel
          label={t('Retain message body')}
          noHelperText
          checked={Boolean(draft.retainBody)}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            set('retainBody', event.currentTarget.checked)
          }
        />
      </div>

      <Button
        variant="contained"
        color="primary"
        disabled={saving}
        className={classes.submit}
        onClick={() => onSubmit(draft)}>
        {saving ? t('Saving…') : t('Save category')}
      </Button>
    </Box>
  )
}
